import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BookEngineNav } from '../components/editor/BookEngineNav';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DescriptionIcon from '@mui/icons-material/Description';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ListAltIcon from '@mui/icons-material/ListAlt';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useParams } from '@tanstack/react-router';
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  applyEdgeChanges,
  applyNodeChanges,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { kmeans } from 'ml-kmeans';
import {
  useAddBoardConnection,
  useAddBoardNote,
  useBoardEmbeddings,
  useBoardsForBook,
  useBoardTemplates,
  useCreateBoard,
  useCreateBoardFromTemplate,
  useDeleteBoardConnection,
  useDeleteBoardNote,
  useUpdateBoardNote,
  type BoardConnectionDto,
  type BoardDto,
  type BoardNoteDto,
} from '../api/queries/boards';
import { useMarkLastEngine } from '../api/queries/bookSession';
import {
  useOutlinesForBook,
  useCreateOutline,
  useAddOutlineNode,
} from '../api/queries/outlines';
import {
  useStoryArchitectSession,
  useCreateSession,
  useCreateBeat,
} from '../api/queries/storyArchitect';
import { FilterableSelect } from '../components/common/FilterableSelect';
import { GenerationModal } from '../components/ai/GenerationModal';

/**
 * Creativity Board canvas. Renders notes as nodes and connections as edges using
 * react-flow. Dragging a node debounces an UpdateBoardNote call (300ms) so we don't
 * spam the API mid-drag. Connecting two nodes creates a BoardConnection; deleting
 * a selected node/edge cascades to the backend.
 */
export function BoardsRoute() {
  const { bookId } = useParams({ strict: false }) as { bookId: string };
  useMarkLastEngine(bookId, 'board');
  const boards = useBoardsForBook(bookId);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const active = useMemo(
    () =>
      boards.data?.find((b) => b.id === activeId) ?? boards.data?.[0] ?? null,
    [boards.data, activeId],
  );

  if (boards.isLoading) return <CircularProgress />;

  return (
    <Stack sx={{ height: 'calc(100vh - 100px)' }}>
      <BookEngineNav bookId={bookId} />
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2, mt: 1 }}
      >
        <Typography variant="h4" sx={{ flex: 1 }}>
          Creativity Board
        </Typography>
        <Stack direction="row" spacing={1}>
          {(boards.data ?? []).map((b) => (
            <Button
              key={b.id}
              size="small"
              variant={active?.id === b.id ? 'contained' : 'outlined'}
              onClick={() => setActiveId(b.id)}
            >
              {b.name}
            </Button>
          ))}
        </Stack>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          New board
        </Button>
      </Stack>

      {!active && (
        <Alert severity="info">
          No boards yet. Create one from scratch or start from a built-in
          template (Character Web, World Map, Plot Lines).
        </Alert>
      )}

      {active && <BoardCanvas bookId={bookId} board={active} />}

      <CreateBoardDialog
        bookId={bookId}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          setActiveId(id);
          setCreateOpen(false);
        }}
      />
    </Stack>
  );
}

function BoardCanvas({ bookId, board }: { bookId: string; board: BoardDto }) {
  const update = useUpdateBoardNote(bookId);
  const addNote = useAddBoardNote(bookId);
  const addConn = useAddBoardConnection(bookId);
  const delNote = useDeleteBoardNote(bookId);
  const delConn = useDeleteBoardConnection(bookId);
  const fetchEmbeddings = useBoardEmbeddings();
  const outlines = useOutlinesForBook(bookId);
  const createOutline = useCreateOutline();
  const addOutlineNode = useAddOutlineNode(bookId);
  const storySession = useStoryArchitectSession(bookId);
  const createSession = useCreateSession(bookId);
  const createBeat = useCreateBeat(bookId);
  const [clusterMsg, setClusterMsg] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Snackbar for toasts
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');

  // Add note dialog state
  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
  const [addNoteTitle, setAddNoteTitle] = useState('');

  // GenerationModal state
  const [genModalOpen, setGenModalOpen] = useState(false);
  const [genModalSeedPrompt, setGenModalSeedPrompt] = useState('');

  // Export dropdown menu state
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
  const exportMenuOpen = Boolean(exportAnchor);

  const [nodes, setNodes] = useState<Node[]>(() =>
    boardNotesToNodes(board.notes),
  );
  const [edges, setEdges] = useState<Edge[]>(() =>
    boardConnsToEdges(board.connections),
  );

  useEffect(() => {
    setNodes(boardNotesToNodes(board.notes));
    setEdges(boardConnsToEdges(board.connections));
  }, [board.id, board.notes, board.connections]);

  // Debounce per-node position commits.
  const pending = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const commitPosition = useCallback(
    (id: string, x: number, y: number) => {
      const existing = pending.current.get(id);
      if (existing) clearTimeout(existing);
      const handle = setTimeout(() => {
        update.mutate({ id, x: Math.round(x), y: Math.round(y) });
        pending.current.delete(id);
      }, 300);
      pending.current.set(id, handle);
    },
    [update],
  );

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setNodes((prev) => {
        const next = applyNodeChanges(changes, prev);
        for (const change of changes) {
          if (
            change.type === 'position' &&
            change.position &&
            !change.dragging
          ) {
            commitPosition(change.id, change.position.x, change.position.y);
          }
          if (change.type === 'remove') {
            delNote.mutate(change.id);
          }
        }
        return next;
      });
    },
    [commitPosition, delNote],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setEdges((prev) => {
        const next = applyEdgeChanges(changes, prev);
        for (const change of changes) {
          if (change.type === 'remove') {
            delConn.mutate(change.id);
          }
        }
        return next;
      });
    },
    [delConn],
  );

  const onConnect = useCallback(
    (conn: Connection) => {
      if (!conn.source || !conn.target || conn.source === conn.target) return;
      addConn.mutate({
        boardId: board.id,
        fromNoteId: conn.source,
        toNoteId: conn.target,
        label: null,
      });
    },
    [addConn, board.id],
  );

  const handleCluster = useCallback(async () => {
    setClusterMsg(null);
    try {
      const result = await fetchEmbeddings.mutateAsync(board.id);
      if (result.disabled) {
        setClusterMsg(
          'Embeddings disabled — enable Google Vertex in config to cluster.',
        );
        return;
      }
      if (result.notes.length < 2) {
        setClusterMsg('Need at least 2 notes with text to cluster.');
        return;
      }
      const vectors = result.notes.map((n) => n.vector);
      const k = Math.max(
        2,
        Math.min(
          result.notes.length,
          Math.round(Math.sqrt(result.notes.length)),
        ),
      );
      const output = kmeans(vectors, k, { seed: 42 });

      // Reposition nodes into k visual groups laid out on a circle; notes in the same
      // cluster are spread along a small radius inside each group.
      const radius = 360;
      const groupRadius = 140;
      const centerX = 500;
      const centerY = 400;
      setNodes((prev) => {
        const moves = new Map<string, { x: number; y: number }>();
        result.notes.forEach((note, i) => {
          const cluster = output.clusters[i];
          const groupAngle = (cluster / k) * Math.PI * 2;
          const groupCx = centerX + radius * Math.cos(groupAngle);
          const groupCy = centerY + radius * Math.sin(groupAngle);
          const indexInCluster = result.notes
            .slice(0, i)
            .filter((_, j) => output.clusters[j] === cluster).length;
          const clusterSize = output.clusters.filter(
            (c) => c === cluster,
          ).length;
          const innerAngle =
            clusterSize > 1 ? (indexInCluster / clusterSize) * Math.PI * 2 : 0;
          const x = groupCx + groupRadius * Math.cos(innerAngle);
          const y = groupCy + groupRadius * Math.sin(innerAngle);
          moves.set(note.noteId, { x: Math.round(x), y: Math.round(y) });
          update.mutate({
            id: note.noteId,
            x: Math.round(x),
            y: Math.round(y),
          });
        });
        return prev.map((n) => {
          const pos = moves.get(n.id);
          return pos ? { ...n, position: pos } : n;
        });
      });
      setClusterMsg(`Clustered ${result.notes.length} notes into ${k} groups.`);
    } catch (ex) {
      setClusterMsg(`Clustering failed: ${(ex as Error).message}`);
    }
  }, [board.id, fetchEmbeddings, update]);

  const handleAddNote = () => {
    setAddNoteTitle('');
    setAddNoteDialogOpen(true);
  };

  const handleAddNoteConfirm = () => {
    if (!addNoteTitle.trim()) return;
    addNote.mutate({
      boardId: board.id,
      kind: 'note',
      content: { title: addNoteTitle.trim(), body: '' },
      x: 200 + Math.random() * 100,
      y: 200 + Math.random() * 100,
    });
    setAddNoteDialogOpen(false);
    setAddNoteTitle('');
  };

  /* ----- AI action helpers ----- */

  const collectNoteSummaries = useCallback((): string => {
    return board.notes
      .map((n) => {
        const content = n.content as {
          title?: string;
          body?: string;
          name?: string;
        } | null;
        const title = content?.title ?? content?.name ?? '(untitled)';
        const body = content?.body ?? '';
        return body ? `${title}: ${body}` : title;
      })
      .join('\n- ');
  }, [board.notes]);

  const collectConnectionSummaries = useCallback((): string => {
    return board.connections
      .map((c) => {
        const fromNote = board.notes.find((n) => n.id === c.fromNoteId);
        const toNote = board.notes.find((n) => n.id === c.toNoteId);
        const fromTitle =
          (fromNote?.content as { title?: string } | null)?.title ??
          c.fromNoteId;
        const toTitle =
          (toNote?.content as { title?: string } | null)?.title ?? c.toNoteId;
        const label = c.label ? ` (${c.label})` : '';
        return `${fromTitle} -> ${toTitle}${label}`;
      })
      .join('\n- ');
  }, [board.notes, board.connections]);

  const handleGenerateIdeas = useCallback(() => {
    if (board.notes.length === 0) {
      setSnackMessage(
        'Add some notes first so the AI has context to work with.',
      );
      setSnackOpen(true);
      return;
    }
    const noteSummaries = collectNoteSummaries();
    const prompt = `Based on these brainstorming notes, suggest 3-5 new ideas that would complement or extend the existing concepts. Present each as a short sticky note (title + 1-2 sentences).\n\nExisting notes:\n- ${noteSummaries}`;
    setGenModalSeedPrompt(prompt);
    setGenModalOpen(true);
  }, [board.notes.length, collectNoteSummaries]);

  const handleCreativeBrief = useCallback(() => {
    if (board.notes.length === 0) {
      setSnackMessage(
        'Add some notes first so the AI has context to work with.',
      );
      setSnackOpen(true);
      return;
    }
    const noteSummaries = collectNoteSummaries();
    const connectionSummaries = collectConnectionSummaries();
    let prompt = `Synthesize these brainstorming notes and their connections into a coherent creative brief (3-5 paragraphs). Identify key themes, conflicts, and narrative opportunities.\n\nNotes:\n- ${noteSummaries}`;
    if (connectionSummaries) {
      prompt += `\n\nConnections:\n- ${connectionSummaries}`;
    }
    setGenModalSeedPrompt(prompt);
    setGenModalOpen(true);
  }, [board.notes.length, collectNoteSummaries, collectConnectionSummaries]);

  const handleSuggestConnections = useCallback(() => {
    setSnackMessage(
      'Connection suggestions coming soon — uses embedding similarity to find related notes',
    );
    setSnackOpen(true);
  }, []);

  const handleExportToOutline = useCallback(async () => {
    setExportAnchor(null);
    if (!board || board.notes.length === 0) {
      setSnackMessage('No notes to export');
      setSnackOpen(true);
      return;
    }
    setExporting(true);
    try {
      let outlineId = outlines.data?.[0]?.id;
      if (!outlineId) {
        const created = await createOutline.mutateAsync({
          projectId: bookId,
          name: 'From Board',
        });
        outlineId = created.id;
      }
      for (const note of board.notes) {
        const content = note.content as {
          title?: string;
          body?: string;
          name?: string;
        } | null;
        const title =
          content?.title ?? content?.name ?? `Note ${note.id.slice(0, 8)}`;
        const description = content?.body ?? undefined;
        await addOutlineNode.mutateAsync({
          outlineId,
          parentId: null,
          name: title,
          description,
        });
      }
      setSnackMessage(
        `Created ${board.notes.length} outline nodes from board notes`,
      );
      setSnackOpen(true);
    } catch {
      setSnackMessage('Failed to export notes to outline');
      setSnackOpen(true);
    } finally {
      setExporting(false);
    }
  }, [board, bookId, outlines.data, createOutline, addOutlineNode]);

  const handleExportToBeats = useCallback(async () => {
    setExportAnchor(null);
    if (!board || board.notes.length === 0) {
      setSnackMessage('No notes to export');
      setSnackOpen(true);
      return;
    }
    setExporting(true);
    try {
      let sessionId = storySession.data?.id;
      if (!sessionId) {
        const created = await createSession.mutateAsync({
          name: 'From Board',
        });
        sessionId = created.id;
      }
      for (const note of board.notes) {
        const content = note.content as {
          title?: string;
          body?: string;
          name?: string;
        } | null;
        const title =
          content?.title ?? content?.name ?? `Note ${note.id.slice(0, 8)}`;
        const description = content?.body ?? undefined;
        await createBeat.mutateAsync({
          sessionId,
          name: title,
          description,
        });
      }
      setSnackMessage(`Created ${board.notes.length} beats from board notes`);
      setSnackOpen(true);
    } catch {
      setSnackMessage('Failed to export notes to beats');
      setSnackOpen(true);
    } finally {
      setExporting(false);
    }
  }, [board, storySession.data, createSession, createBeat]);

  return (
    <Box
      sx={{
        flex: 1,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        position: 'relative',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          zIndex: 10,
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.5,
        }}
      >
        <Button variant="contained" size="small" onClick={handleAddNote}>
          + Note
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleCluster}
          disabled={fetchEmbeddings.isPending}
        >
          {fetchEmbeddings.isPending
            ? 'Clustering...'
            : 'Cluster by similarity'}
        </Button>

        {/* AI action buttons */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<AutoFixHighIcon />}
          onClick={handleGenerateIdeas}
        >
          Generate Ideas
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DescriptionIcon />}
          onClick={handleCreativeBrief}
        >
          Creative Brief
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DeviceHubIcon />}
          onClick={handleSuggestConnections}
        >
          Suggest Connections
        </Button>

        {/* Export dropdown */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          disabled={exporting}
          onClick={(e) => setExportAnchor(e.currentTarget)}
        >
          {exporting ? 'Exporting...' : 'Export'}
        </Button>
        <Menu
          anchorEl={exportAnchor}
          open={exportMenuOpen}
          onClose={() => setExportAnchor(null)}
        >
          <MenuItem onClick={handleExportToOutline}>
            <ListItemIcon>
              <ListAltIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Export to Outline" />
          </MenuItem>
          <MenuItem onClick={handleExportToBeats}>
            <ListItemIcon>
              <TimelineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Export to Beats" />
          </MenuItem>
        </Menu>

        {clusterMsg && (
          <Alert
            severity="info"
            sx={{ py: 0 }}
            onClose={() => setClusterMsg(null)}
          >
            {clusterMsg}
          </Alert>
        )}
      </Stack>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <Background />
        <Controls />
        <MiniMap pannable zoomable />
      </ReactFlow>

      {/* Generation Modal for AI actions */}
      <GenerationModal
        open={genModalOpen}
        onClose={() => setGenModalOpen(false)}
        mode="text"
        seedPrompt={genModalSeedPrompt}
        bookId={bookId}
      />

      {/* Toast notifications */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={5000}
        onClose={() => setSnackOpen(false)}
        message={snackMessage}
      />

      {/* Add note dialog */}
      <Dialog
        open={addNoteDialogOpen}
        onClose={() => setAddNoteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Note title"
            value={addNoteTitle}
            onChange={(e) => setAddNoteTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddNoteConfirm();
            }}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddNoteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddNoteConfirm}
            disabled={!addNoteTitle.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function boardNotesToNodes(notes: BoardNoteDto[]): Node[] {
  return notes.map((n) => {
    const content =
      (n.content as { title?: string; body?: string; name?: string } | null) ??
      {};
    const label = content.title ?? content.name ?? '(untitled)';
    const body = content.body;
    return {
      id: n.id,
      position: { x: n.x, y: n.y },
      data: {
        label: (
          <Box sx={{ p: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {label}
            </Typography>
            {body && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                {body}
              </Typography>
            )}
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', mt: 0.5 }}
            >
              {n.kind}
            </Typography>
          </Box>
        ),
      },
      style: {
        width: n.width,
        minHeight: n.height,
        background: colorForKind(n.kind),
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: 6,
      },
    };
  });
}

function boardConnsToEdges(conns: BoardConnectionDto[]): Edge[] {
  return conns.map((c) => ({
    id: c.id,
    source: c.fromNoteId,
    target: c.toNoteId,
    label: c.label ?? undefined,
    animated: false,
  }));
}

function colorForKind(kind: string): string {
  switch (kind) {
    case 'entity':
      return '#FFF3E0';
    case 'worldelement':
      return '#E3F2FD';
    default:
      return '#FFFDE7';
  }
}

function CreateBoardDialog({
  bookId,
  open,
  onClose,
  onCreated,
}: {
  bookId: string;
  open: boolean;
  onClose: () => void;
  onCreated: (boardId: string) => void;
}) {
  const templates = useBoardTemplates();
  const create = useCreateBoard();
  const fromTemplate = useCreateBoardFromTemplate();
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('');

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New board</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <FilterableSelect
            label="Template (optional)"
            value={templateId}
            onChange={(newValue) => setTemplateId(newValue)}
            allowEmpty={true}
            emptyLabel="(blank board)"
            options={(templates.data ?? []).map((t) => ({
              value: t.id,
              label: `${t.name} (${t.noteCount} notes)`,
            }))}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || create.isPending || fromTemplate.isPending}
          onClick={async () => {
            const board = templateId
              ? await fromTemplate.mutateAsync({
                  templateId,
                  projectId: bookId,
                  name: name.trim(),
                })
              : await create.mutateAsync({
                  projectId: bookId,
                  name: name.trim(),
                });
            onCreated(board.id);
            setName('');
            setTemplateId('');
          }}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
